import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { UWQueueAgentRow } from "@/models/underwriting";
import { CollapsibleSection } from "./CollapsibleSection";
import { underwritingService } from "@/services/UnderwritingService";
import { useApi } from "@/hooks/useApi";
import { Spinner } from "@/components/ui/spinner";
import { agentTotals, formatDuration, unactionedFor } from "@/helpers/underwritingQueueDashboard";

interface QueueTeamPerformanceSectionProps {
  start: string;
  end: string;
  refreshKey?: number;
  defaultOpen?: boolean;
}

/**
 * Opened comes from claim history and Allocated/Deaded from the action audit, so
 * Unactioned exposes applications an agent went into but never saw through — the
 * question this table exists to answer.
 */
export function QueueTeamPerformanceSection({
  start,
  end,
  refreshKey,
  defaultOpen,
}: QueueTeamPerformanceSectionProps) {
  const result = useApi(
    (signal) =>
      underwritingService.getQueueTeamPerformance(`${start}T00:00:00`, `${end}T23:59:59`, signal),
    [start, end, refreshKey]
  );

  const agents = useMemo(() => result.data ?? [], [result.data]);
  const totals = useMemo(() => agentTotals(agents), [agents]);

  if (!result.loading && (result.error != null || agents.length === 0)) return null;

  const th = "px-4 py-2.5 text-right text-[12px] font-semibold text-muted-foreground whitespace-nowrap";
  const td = "px-4 py-3 text-right tabular-nums";

  return (
    <CollapsibleSection title="Team Performance" defaultOpen={defaultOpen}>
      {result.loading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground">
                  Agent
                </th>
                <th className={th} title="Applications claimed in this period">
                  Opened
                </th>
                <th className={th}>Allocated</th>
                <th className={th}>Deaded</th>
                <th className={th}>Actioned</th>
                <th className={th} title="Claimed but released without an outcome">
                  Unactioned
                </th>
                <th className={th} title="How long the application had waited before this agent cleared it">
                  Avg Queue Time
                </th>
                <th className={th} title="How long this agent spent on it after claiming">
                  Avg Handling
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent: UWQueueAgentRow) => {
                const unactioned = unactionedFor(agent);
                return (
                  <tr key={agent.agentId} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                      {agent.agentName}
                    </td>
                    <td className={cn(td, "text-foreground")}>{agent.opened}</td>
                    <td className={cn(td, "text-foreground")}>{agent.allocated}</td>
                    <td className={cn(td, "text-foreground")}>{agent.deaded}</td>
                    <td className={cn(td, "font-semibold text-foreground")}>{agent.totalActioned}</td>
                    <td className={cn(td, unactioned > 0 ? "font-medium text-amber-600" : "text-muted-foreground")}>
                      {unactioned}
                    </td>
                    <td className={cn(td, "text-muted-foreground")}>
                      {formatDuration(agent.avgQueueMinutes)}
                    </td>
                    <td className={cn(td, "text-muted-foreground")}>
                      {formatDuration(agent.avgHandlingMinutes, "0m")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td className="bg-slate-100 px-4 py-3 text-[12px] font-semibold text-foreground">Total</td>
                <td className={cn(td, "bg-slate-100 font-semibold text-foreground")}>{totals.opened}</td>
                <td className={cn(td, "bg-slate-100 font-semibold text-foreground")}>{totals.allocated}</td>
                <td className={cn(td, "bg-slate-100 font-semibold text-foreground")}>{totals.deaded}</td>
                <td className={cn(td, "bg-slate-100 font-semibold text-foreground")}>{totals.totalActioned}</td>
                <td className={cn(td, "bg-slate-100 font-semibold text-foreground")}>{totals.unactioned}</td>
                {/* Averaging the per-agent averages would be misleading without row weights. */}
                <td className="bg-slate-100 px-4 py-3" />
                <td className="bg-slate-100 px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}
